import React, { useMemo, useState } from "react";

export type ReportStageStat = {
  key: string;
  label: string;
  value: number | string;
  color: string;
  bg: string;
  Icon: React.ComponentType<{ className?: string; size?: number }>;
};

type Props = {
  total: number | string | null | undefined;
  stats: ReportStageStat[];
  loading?: boolean;
  /** Shown under Grand Total */
  totalLabel?: string;
};

const formatCount = (value: any) => {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value ?? 0);
  return n.toLocaleString("en-IN");
};

const prettyLabel = (raw: string) => {
  const s = String(raw || "").trim();
  if (!s || /^null$/i.test(s) || s.toLowerCase() === "undefined") {
    return "Uncategorized";
  }
  return s
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Build sorted stage list from API header object. */
export function buildStageStatsFromHeader(
  headerData: Record<string, any> | null | undefined,
  resolveStyle: (
    label: string,
    idx: number
  ) => { color: string; bg: string; Icon: ReportStageStat["Icon"] }
): ReportStageStat[] {
  if (!headerData) return [];
  const source =
    headerData.stageTotals && typeof headerData.stageTotals === "object"
      ? headerData.stageTotals
      : headerData;
  return Object.entries(source)
    .filter(
      ([key, value]) =>
        key !== "total_count" &&
        key !== "stageTotals" &&
        (typeof value === "number" ||
          (typeof value === "string" && value !== "" && !Number.isNaN(Number(value))))
    )
    .map(([key, value], idx) => {
      const label = prettyLabel(String(key));
      const style = resolveStyle(label, idx);
      return {
        key,
        label,
        value: Number(value) || 0,
        color: style.color,
        bg: style.bg,
        Icon: style.Icon,
      };
    })
    .sort((a, b) => Number(b.value) - Number(a.value));
}

/**
 * Compact report summary: hero total + top stages + dense scrollable breakdown.
 * Avoids the old wall of equal-size chips that forced page scroll.
 */
export default function ReportStageSummary({
  total,
  stats,
  loading,
  totalLabel = "Leads in range",
}: Props) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);

  const totalNum = Number(total) || 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return stats;
    return stats.filter((s) => s.label.toLowerCase().includes(q));
  }, [stats, query]);

  const top = filtered.slice(0, 5);
  const rest = filtered.slice(5);
  const hiddenCount = rest.length;

  if (loading) {
    return (
      <div className="mb-4 grid shrink-0 grid-cols-1 gap-4 xl:grid-cols-[220px_minmax(0,1fr)]">
        <div className="h-[140px] animate-pulse rounded-2xl bg-[#E8EAF8]" />
        <div className="h-[140px] animate-pulse rounded-2xl bg-[#EEF0F5]" />
      </div>
    );
  }

  return (
    <div className="mb-4 grid shrink-0 grid-cols-1 gap-4 xl:grid-cols-[220px_minmax(0,1fr)] 2xl:grid-cols-[240px_minmax(0,1fr)]">
      {/* Grand total */}
      <div className="relative overflow-hidden rounded-2xl bg-[#A02448] p-5 text-white shadow-[0_8px_24px_rgba(91,95,239,0.28)]">
        <p className="text-sm font-medium text-white/80">Grand Total</p>
        <p className="mt-2 text-4xl font-bold tracking-tight tabular-nums">
          {formatCount(totalNum)}
        </p>
        <p className="mt-1 text-sm text-white/75">{totalLabel}</p>
        <p className="mt-3 text-xs text-white/65">
          {stats.length} stage{stats.length === 1 ? "" : "s"} tracked
        </p>
        <div
          className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10"
          aria-hidden
        />
      </div>

      {/* Breakdown */}
      <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-5">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-[#111827]">
              Stage breakdown
            </p>
            <p className="text-xs text-[#9CA3AF]">
              Sorted by volume · share of grand total
            </p>
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter stages…"
            className="h-9 w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF] focus:border-[#A02448] focus:ring-2 focus:ring-[#A02448]/20 sm:w-[200px]"
          />
        </div>

        {filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#9CA3AF]">
            No stages match your filter.
          </p>
        ) : (
          <>
            {/* Top highlight cards */}
            <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              {top.map((stat) => {
                const Icon = stat.Icon;
                const pct =
                  totalNum > 0
                    ? Math.round((Number(stat.value) / totalNum) * 1000) / 10
                    : 0;
                return (
                  <div
                    key={stat.key}
                    className="rounded-xl border border-[#F1F3F9] bg-[#FAFBFC] px-3 py-2.5"
                    title={`${stat.label}: ${formatCount(stat.value)} (${pct}%)`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                        style={{ backgroundColor: stat.bg, color: stat.color }}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <p
                        className="min-w-0 truncate text-[11px] font-medium leading-tight text-[#6B7280]"
                        title={stat.label}
                      >
                        {stat.label}
                      </p>
                    </div>
                    <p className="text-lg font-bold tabular-nums leading-none text-[#111827]">
                      {formatCount(stat.value)}
                    </p>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#EEF0F5]">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, pct)}%`,
                          backgroundColor: stat.color,
                        }}
                      />
                    </div>
                    <p className="mt-1 text-[10px] font-medium tabular-nums text-[#9CA3AF]">
                      {pct}%
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Dense list for remaining / all when expanded */}
            {(hiddenCount > 0 || expanded) && (
              <div className="rounded-xl border border-[#F1F3F9] bg-[#FAFBFC]">
                <div className="flex items-center justify-between border-b border-[#F1F3F9] px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
                    {expanded
                      ? `All remaining stages (${rest.length})`
                      : `${hiddenCount} more stages`}
                  </p>
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-xs font-semibold text-[#A02448] hover:underline"
                  >
                    {expanded ? "Show less" : "Show all"}
                  </button>
                </div>

                {expanded ? (
                  <div className="max-h-[200px] overflow-y-auto overscroll-contain [scrollbar-width:thin]">
                    <ul className="divide-y divide-[#F1F3F9]">
                      {rest.map((stat) => {
                        const Icon = stat.Icon;
                        const pct =
                          totalNum > 0
                            ? Math.round(
                                (Number(stat.value) / totalNum) * 1000
                              ) / 10
                            : 0;
                        return (
                          <li
                            key={stat.key}
                            className="flex items-center gap-3 px-3 py-2"
                          >
                            <span
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                              style={{
                                backgroundColor: stat.bg,
                                color: stat.color,
                              }}
                            >
                              <Icon className="h-3.5 w-3.5" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex items-center justify-between gap-2">
                                <p
                                  className="truncate text-xs font-medium text-[#374151]"
                                  title={stat.label}
                                >
                                  {stat.label}
                                </p>
                                <p className="shrink-0 text-xs font-semibold tabular-nums text-[#111827]">
                                  {formatCount(stat.value)}
                                  <span className="ml-1 font-normal text-[#9CA3AF]">
                                    · {pct}%
                                  </span>
                                </p>
                              </div>
                              <div className="h-1 overflow-hidden rounded-full bg-[#EEF0F5]">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${Math.min(100, pct)}%`,
                                    backgroundColor: stat.color,
                                  }}
                                />
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <p className="px-3 py-2.5 text-xs text-[#6B7280]">
                    Top 5 stages shown above. Expand to see the full list
                    without crowding the page.
                  </p>
                )}
              </div>
            )}

            {/* When ≤5 stages, still show compact list for detail */}
            {hiddenCount === 0 && filtered.length > 0 && filtered.length <= 5 ? (
              <p className="mt-1 text-[11px] text-[#9CA3AF]">
                Showing all {filtered.length} stages.
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
